import type { KeyboardShortcut } from '@unifyapps/defs/blocks/common/keyboardShortcut';

const isMac = typeof window !== 'undefined' && /Mac|iPod|iPhone|iPad/.test(navigator.platform);

const SPECIAL_KEYS_LABELS: Partial<Record<string, string>> = {
  ' ': 'Space',
  ArrowLeft: '←',
  ArrowRight: '→',
  ArrowUp: '↑',
  ArrowDown: '↓',
  Backspace: '⌫',
  Enter: '⏎',
  Return: '⏎',
  Delete: '⌦',
  Tab: '⇥',
  Escape: 'Esc',
};

const MODIFIER_SYMBOLS = {
  metaKey: isMac ? '⌘' : 'Win',
  ctrlKey: '⌃',
  altKey: isMac ? '⌥' : 'Alt',
  shiftKey: '⇧',
};
const MODIFIER_KEYS = ['shiftKey', 'ctrlKey', 'altKey', 'metaKey'] as const;

const KEY_ALIASES_CONFIG: Partial<Record<string, string>> = {
  Meta: 'Meta',
  Command: 'Meta',
  Cmd: 'Meta',
  OS: 'Meta',
  Windows: 'Meta',
  Control: 'Control',
  Ctrl: 'Control',
  Alt: 'Alt',
  Option: 'Alt',
  Shift: 'Shift',
};

const MODIFIER_KEYS_SET = new Set(Object.values(KEY_ALIASES_CONFIG));

const normalizeKey = (key: string): string => KEY_ALIASES_CONFIG[key] || key;

/**
 * Creates a keyboard shortcut object from a keyboard event,
 * only including active modifiers
 */
export const getKeyboardShortcutFromEvent = (
  event: React.KeyboardEvent<HTMLElement> | KeyboardEvent,
): KeyboardShortcut => {
  const modifiers = MODIFIER_KEYS.reduce<Partial<KeyboardShortcut>>((acc, key) => {
    event[key] && (acc[key] = true);
    return acc;
  }, {});

  return { key: normalizeKey(event.key), ...modifiers };
};

/**
 * Converts a keyboard shortcut to a human-readable string with
 * platform-specific symbols
 */
export const getKeyboardShortcutLabel = (shortcut: KeyboardShortcut | undefined): string => {
  if (!shortcut) {
    return '';
  }

  const normalizedKey = normalizeKey(shortcut.key);
  const keys: string[] = [];
  const isModifierKey = MODIFIER_KEYS_SET.has(normalizedKey);

  for (const modifier of MODIFIER_KEYS) {
    if (shortcut[modifier]) {
      keys.push(MODIFIER_SYMBOLS[modifier]);
    }
  }

  if (!isModifierKey) {
    const displayKey = SPECIAL_KEYS_LABELS[normalizedKey] || normalizedKey;
    keys.push(normalizedKey.length === 1 ? displayKey.toUpperCase() : displayKey);
  }

  return keys.join('');
};

export const isKeyboardShortcutEvent = (
  event: KeyboardEvent,
  shortcut: KeyboardShortcut,
): boolean => {
  return (
    event.key === shortcut.key &&
    event.shiftKey === Boolean(shortcut.shiftKey) &&
    event.ctrlKey === Boolean(shortcut.ctrlKey) &&
    event.altKey === Boolean(shortcut.altKey) &&
    event.metaKey === Boolean(shortcut.metaKey)
  );
};
